/**
 * Standalone Overlay Tool View
 * Mass-apply branding (Logo, Overlay, Watermark) to images
 * Full-featured version with Library support
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { 
  Upload, 
  Image as ImageIcon, 
  Download, 
  Trash2, 
  RefreshCw,
  AlignStartVertical,
  AlignEndVertical,
  AlignStartHorizontal,
  AlignEndHorizontal,
  X,
  Loader2,
  ArrowLeft,
  Check,
  Droplets,
  Grid3X3,
  Square,
  CornerLeftUp,
  CornerRightUp,
  CornerLeftDown,
  CornerRightDown,
  Crop,
  Move,
  Sparkles,
} from 'lucide-react';
import { saveAs } from 'file-saver';
import { applyAiLabelToImage, renderExportPreview, BrandingLayerError, processWatermark, WatermarkOptions, cropToAspectRatio, imageMatchesAspectRatio, fetchImageAsBlob, downscaleForPreview, renderBrandedExport, type BrandingOptions } from '@/services/imageProcessing';
import { assembleExportZip, type PlannedFile } from '@/services/exportService';
import { AiLabelOption, useAiLabelPrefs } from '@/components/ui/AiLabelOption';
import { fetchProjectGeneratedImages } from '@/services/projectService';
import { fetchBrandingAssets } from '@/services/brandingService';
import { fetchUserProfile, updateDefaultBrandingDefaults, type DefaultBrandingSettings } from '@/services/paymentService';
import { AspectRatio, LogoPosition, WatermarkPosition, BrandingConfig, BrandingAsset } from '@/types';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";
import { overlaysForSelection, resolveOverlayUrlForAspect, overlayNeedsAspectWarning, ASPECT_VALUE } from "@/lib/overlayVariants";

interface UploadedFile {
  id: string;
  file?: File;
  previewUrl: string;
  originalUrl: string; // Always the original (for re-cropping)
  name: string;
  isFromProject?: boolean;
  variant?: string;
  cropOffset?: { x: number; y: number };
  cropScale?: number; // 0.1-1.0, 1 = max crop area
  needsCrop?: boolean; // Whether the image was cropped to fit aspect ratio
}

/**
 * Trägt dieses Bild ein KI-Ergebnis und damit den Hinweis "Mit KI erstellt"?
 *
 * Nur Projektbilder: die kommen aus fetchProjectGeneratedImages, das
 * ausschließlich generierte Storage-Pfade liefert (leer/eingerichtet/
 * gebrandet/Grundriss) — der Original-Upload (storage_path) ist dort nicht
 * dabei. Selbst hochgeladene Dateien sind dagegen Fotos des Maklers, die das
 * Tool nur brandet; die bleiben unmarkiert.
 */
export const overlayImageIsAiResult = (img: { isFromProject?: boolean }): boolean =>
  img.isFromProject === true;

// Vorschau-Crops klein halten: Full-Res-PNG-Data-URLs großer Fotos im State
// frieren die Seite ein — volle Auflösung wird erst beim Export gerechnet.
const PREVIEW_CROP_OUTPUT = { maxDim: 1600, format: 'jpeg', quality: 0.9, asBlobUrl: true } as const;

// Crop rectangle in percentage space (0-1) relative to the original image
interface CropRect {
  x: number; // left edge (0-1)
  y: number; // top edge (0-1)
  w: number; // width (0-1)
  h: number; // height (0-1)
}

interface OverlayToolViewProps {
  projectId?: string;
  projectName?: string;
  onBack?: () => void;
}

const DEFAULT_BRANDING: BrandingConfig = {
  logo: null,
  logoUrl: null,
  overlay: null,
  overlayUrl: null,
  logoPosition: 'bottom-right',
  watermarkUrl: null,
  watermarkOpacity: 30,
  watermarkPosition: 'center',
  watermarkScale: 0.5,
};

export const OverlayToolView: React.FC<OverlayToolViewProps> = ({
  projectId,
  projectName,
  onBack,
}) => {
  const { user } = useAuth();

  // Aspect Ratio State
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>('4:3');
  const [aspectRatioLoaded, setAspectRatioLoaded] = useState(false);

  // Crop Modal State
  const [cropModalImage, setCropModalImage] = useState<UploadedFile | null>(null);
  const [cropImgDims, setCropImgDims] = useState<{ w: number; h: number } | null>(null);
  const [cropRect, setCropRect] = useState<CropRect>({ x: 0, y: 0, w: 1, h: 1 });

  // Images State
  const [uploadedImages, setUploadedImages] = useState<UploadedFile[]>([]);
  const [isLoadingProjectImages, setIsLoadingProjectImages] = useState(false);
  const aiLabel = useAiLabelPrefs();
  // Der Hinweis gilt nur für Projektbilder — Anzahl fürs Hinweis-Label.
  const aiLabeledCount = useMemo(() => uploadedImages.filter(overlayImageIsAiResult).length, [uploadedImages]);

  // Branding State
  const [branding, setBranding] = useState<BrandingConfig>(DEFAULT_BRANDING);
  
  // Asset Library State
  const [logos, setLogos] = useState<BrandingAsset[]>([]);
  const [overlays, setOverlays] = useState<BrandingAsset[]>([]);
  const [watermarks, setWatermarks] = useState<BrandingAsset[]>([]);
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);

  // Selected Asset IDs
  const [selectedLogoId, setSelectedLogoId] = useState<string | null>(null);
  const [selectedOverlayId, setSelectedOverlayId] = useState<string | null>(null);
  const [selectedWatermarkId, setSelectedWatermarkId] = useState<string | null>(null);

  // Processing State
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  
  // Watermark preview
  const [watermarkPreviewUrl, setWatermarkPreviewUrl] = useState<string | null>(null);

  // Große WYSIWYG-Vorschau: läuft über renderExportPreview (= exakt der Export-Pfad)
  const [previewImageId, setPreviewImageId] = useState<string | null>(null);
  const [finalPreview, setFinalPreview] = useState<{ url: string; failedLayers: string[] } | null>(null);
  const [isRenderingPreview, setIsRenderingPreview] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // ============================================================================
  // Load Assets, Profile & Branding Defaults on Mount
  // ============================================================================

  useEffect(() => {
    if (!user) return;

    const loadAll = async () => {
      setIsLoadingAssets(true);
      try {
        // Check if user is a team member - if so, load admin's assets & defaults
        let assetOwnerId = user.id;
        const { data: adminSettingsData, error: adminSettingsError } = await supabase
          .rpc('get_admin_settings');

        const isTeamMember = !adminSettingsError && adminSettingsData && adminSettingsData.length > 0;
        if (isTeamMember) {
          assetOwnerId = adminSettingsData[0].admin_id;
        }

        // Load assets and profile in parallel
        const [logoData, overlayData, watermarkData, profile] = await Promise.all([
          fetchBrandingAssets(assetOwnerId, 'logo'),
          fetchBrandingAssets(assetOwnerId, 'overlay'),
          fetchBrandingAssets(assetOwnerId, 'watermark'),
          fetchUserProfile(user.id),
        ]);

        setLogos(logoData);
        setOverlays(overlayData);
        setWatermarks(watermarkData);

        // Apply aspect ratio
        if (profile?.defaultAspectRatio) {
          setAspectRatio(profile.defaultAspectRatio);
        }

        // Apply branding defaults
        if (profile || isTeamMember) {
          // For team members, use admin's branding defaults from RPC
          const effectiveDefaults = isTeamMember ? {
            logoPosition: (adminSettingsData[0].default_logo_position as LogoPosition) || 'bottom-right',
            watermarkOpacity: adminSettingsData[0].default_watermark_opacity ?? 30,
            watermarkScale: (adminSettingsData[0].default_watermark_scale ?? 50) / 100,
            watermarkPosition: (adminSettingsData[0].default_watermark_position as WatermarkPosition) || 'center',
            logoAssetId: adminSettingsData[0].default_logo_asset_id as string | null,
            overlayAssetId: adminSettingsData[0].default_overlay_asset_id as string | null,
            watermarkAssetId: adminSettingsData[0].default_watermark_asset_id as string | null,
          } : profile ? {
            logoPosition: profile.defaultLogoPosition,
            watermarkOpacity: profile.defaultWatermarkOpacity,
            watermarkScale: profile.defaultWatermarkScale,
            watermarkPosition: profile.defaultWatermarkPosition,
            logoAssetId: profile.defaultLogoAssetId,
            overlayAssetId: profile.defaultOverlayAssetId,
            watermarkAssetId: profile.defaultWatermarkAssetId,
          } : null;

          if (effectiveDefaults) {
            const defaults: Partial<BrandingConfig> = {
              logoPosition: effectiveDefaults.logoPosition,
              watermarkOpacity: effectiveDefaults.watermarkOpacity,
              watermarkScale: effectiveDefaults.watermarkScale,
              watermarkPosition: effectiveDefaults.watermarkPosition,
            };

            // Resolve default asset IDs to URLs
            if (effectiveDefaults.logoAssetId) {
              const asset = logoData.find(a => a.id === effectiveDefaults.logoAssetId);
              if (asset) {
                defaults.logoUrl = asset.publicUrl;
                setSelectedLogoId(asset.id);
              }
            }
            if (effectiveDefaults.overlayAssetId) {
              const asset = overlayData.find(a => a.id === effectiveDefaults.overlayAssetId);
              if (asset) {
                defaults.overlayUrl = asset.publicUrl;
                setSelectedOverlayId(asset.id);
              }
            }
            if (effectiveDefaults.watermarkAssetId) {
              const asset = watermarkData.find(a => a.id === effectiveDefaults.watermarkAssetId);
              if (asset) {
                defaults.watermarkUrl = asset.publicUrl;
                setSelectedWatermarkId(asset.id);
              }
            }

            setBranding(prev => ({ ...prev, ...defaults }));
          }
        }
      } catch (error) {
        console.error('[OverlayTool] Error loading assets/defaults:', error);
      } finally {
        setIsLoadingAssets(false);
        setAspectRatioLoaded(true);
      }
    };

    loadAll();
  }, [user]);

  // ============================================================================
  // Load Project Images (if projectId provided)
  // ============================================================================

  useEffect(() => {
    if (!projectId || !aspectRatioLoaded) return;

    let cancelled = false;
    const blobUrls: string[] = [];

    const loadProjectImages = async () => {
      setIsLoadingProjectImages(true);
      try {
        const images = await fetchProjectGeneratedImages(projectId);

        const projectImages: UploadedFile[] = await Promise.all(
          images.map(async (img, index) => {
            const id = `project-img-${index}-${Date.now()}-${img.variant}`;

            // Fetch the remote image as a local blob URL to avoid CORS canvas taint
            let originalUrl: string;
            try {
              originalUrl = await fetchImageAsBlob(img.url);
              blobUrls.push(originalUrl);
            } catch (e) {
              console.warn('[OverlayTool] Could not fetch image as blob, using remote URL:', e);
              originalUrl = img.url;
            }

            // Check if image needs cropping
            let previewUrl = originalUrl;
            let needsCrop = false;
            try {
              const matches = await imageMatchesAspectRatio(originalUrl, aspectRatio);
              if (!matches) {
                previewUrl = await cropToAspectRatio(originalUrl, aspectRatio, { x: 0.5, y: 0.5 }, 1.0, PREVIEW_CROP_OUTPUT);
                needsCrop = true;
              }
            } catch (e) {
              console.warn('[OverlayTool] Could not crop project image:', e);
            }

            return {
              id,
              previewUrl,
              originalUrl,
              name: img.name,
              isFromProject: true,
              variant: img.variant,
              needsCrop,
              cropOffset: { x: 0.5, y: 0.5 },
            };
          })
        );

        if (!cancelled) {
          setUploadedImages(projectImages);
        } else {
          blobUrls.forEach(url => URL.revokeObjectURL(url));
        }
      } catch (error) {
        console.error('Fehler beim Laden der Projektbilder:', error);
      } finally {
        if (!cancelled) {
          setIsLoadingProjectImages(false);
        }
      }
    };

    loadProjectImages();

    return () => {
      cancelled = true;
    };
  }, [projectId, aspectRatioLoaded, aspectRatio]);

  // ============================================================================
  // Generate Watermark Preview
  // ============================================================================

  useEffect(() => {
    const generatePreview = async () => {
      if (!branding.watermarkUrl) {
        setWatermarkPreviewUrl(null);
        return;
      }

      try {
        const options: WatermarkOptions = {
          opacity: branding.watermarkOpacity,
          position: branding.watermarkPosition,
          scale: branding.watermarkScale,
        };
        
        const processedCanvas = await processWatermark(
          branding.watermarkUrl,
          200,
          200,
          options
        );
        
        setWatermarkPreviewUrl(processedCanvas.toDataURL());
      } catch (error) {
        console.error('Error generating watermark preview:', error);
      }
    };

    generatePreview();
  }, [branding.watermarkUrl, branding.watermarkOpacity, branding.watermarkPosition, branding.watermarkScale]);

  // ============================================================================
  // Große Export-Vorschau (WYSIWYG)
  // ============================================================================

  // Overlay auf das Ziel-Format auflösen — identisch zum Export-Pfad.
  const selectedOverlayAsset = useMemo(
    () => (selectedOverlayId ? overlays.find((o) => o.id === selectedOverlayId) ?? null : null),
    [selectedOverlayId, overlays]
  );
  const resolvedOverlayUrl = useMemo(
    () => (selectedOverlayAsset ? resolveOverlayUrlForAspect(selectedOverlayAsset, overlays, aspectRatio) : branding.overlayUrl),
    [selectedOverlayAsset, overlays, aspectRatio, branding.overlayUrl]
  );
  const exportBrandingOptions = useMemo<BrandingOptions>(
    () => ({
      logoUrl: branding.logoUrl,
      overlayUrl: resolvedOverlayUrl,
      logoPosition: branding.logoPosition,
      watermarkUrl: branding.watermarkUrl,
      watermarkOpacity: branding.watermarkOpacity,
      watermarkPosition: branding.watermarkPosition,
      watermarkScale: branding.watermarkScale,
    }),
    [branding.logoUrl, resolvedOverlayUrl, branding.logoPosition, branding.watermarkUrl, branding.watermarkOpacity, branding.watermarkPosition, branding.watermarkScale]
  );

  const previewImage = useMemo(
    () => uploadedImages.find((i) => i.id === previewImageId) ?? uploadedImages[0] ?? null,
    [uploadedImages, previewImageId]
  );

  // Herunterskalierte Vorschau-Basis (max. 1600px), einmal pro Quell-URL —
  // Compositing in voller Auflösung friert die Seite bei großen Uploads ein.
  // Key = previewUrl, damit ein neuer Crop automatisch neu skaliert wird.
  const previewBaseCache = useRef(new Map<string, string>());
  const getPreviewBase = useCallback(async (srcUrl: string): Promise<string> => {
    const cached = previewBaseCache.current.get(srcUrl);
    if (cached) return cached;
    const small = await downscaleForPreview(srcUrl, 1600);
    previewBaseCache.current.set(srcUrl, small);
    return small;
  }, []);
  useEffect(
    () => () => {
      for (const [src, small] of previewBaseCache.current) {
        if (small !== src && small.startsWith('blob:')) URL.revokeObjectURL(small);
      }
      previewBaseCache.current.clear();
    },
    []
  );

  // Debounced: bei Slider-/Auswahl-Änderungen erst nach kurzer Ruhe rendern.
  useEffect(() => {
    if (!previewImage) {
      setFinalPreview(null);
      setIsRenderingPreview(false);
      return;
    }
    let cancelled = false;
    setIsRenderingPreview(true);
    const timer = setTimeout(async () => {
      try {
        const base = await getPreviewBase(previewImage.previewUrl);
        if (cancelled) return;
        // Hinweis nur auf Projektbildern — genau wie beim Export.
        const res = await renderExportPreview(base, exportBrandingOptions, {
          enabled: aiLabel.enabled && overlayImageIsAiResult(previewImage),
          position: aiLabel.position,
          opacity: aiLabel.opacity,
        });
        if (!cancelled) setFinalPreview({ url: res.url, failedLayers: res.failedLayers });
      } catch {
        if (!cancelled) setFinalPreview(null);
      } finally {
        if (!cancelled) setIsRenderingPreview(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [previewImage, exportBrandingOptions, getPreviewBase, aiLabel.enabled, aiLabel.position, aiLabel.opacity]);

  // ============================================================================
  // Image Upload Handlers
  // ============================================================================

  const handleFileSelect = useCallback(async (files: FileList | null) => {
    if (!files) return;

    const imageFiles = Array.from(files).filter(file => file.type.startsWith('image/'));

    const newImages: UploadedFile[] = await Promise.all(
      imageFiles.map(async (file) => {
        const id = `img-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const originalUrl = URL.createObjectURL(file);

        let previewUrl = originalUrl;
        let needsCrop = false;
        try {
          const matches = await imageMatchesAspectRatio(originalUrl, aspectRatio);
          if (!matches) {
            previewUrl = await cropToAspectRatio(originalUrl, aspectRatio, { x: 0.5, y: 0.5 }, 1.0, PREVIEW_CROP_OUTPUT);
            needsCrop = true;
          }
        } catch (e) {
          console.warn('[OverlayTool] Could not crop uploaded image:', e);
        }

        return {
          id,
          file,
          previewUrl,
          originalUrl,
          name: file.name.replace(/\.[^/.]+$/, ''),
          needsCrop,
          cropOffset: { x: 0.5, y: 0.5 },
        };
      })
    );

    setUploadedImages(prev => [...prev, ...newImages]);
  }, [aspectRatio]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const removeImage = useCallback((id: string) => {
    setUploadedImages(prev => {
      const img = prev.find(i => i.id === id);
      if (img) {
        // Revoke blob URLs for both user-uploaded files and project images
        if (img.file || img.isFromProject) {
          URL.revokeObjectURL(img.originalUrl);
        }
        if (img.previewUrl !== img.originalUrl && img.previewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(img.previewUrl);
        }
      }
      return prev.filter(i => i.id !== id);
    });
  }, []);

  const clearAllImages = useCallback(() => {
    uploadedImages.forEach(img => {
      if (img.file || img.isFromProject) {
        URL.revokeObjectURL(img.originalUrl);
      }
      if (img.previewUrl !== img.originalUrl && img.previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(img.previewUrl);
      }
    });
    setUploadedImages([]);
  }, [uploadedImages]);

  // ============================================================================
  // Branding Update Handlers
  // ============================================================================

  const updateBranding = useCallback((updates: Partial<BrandingConfig>) => {
    setBranding(prev => ({ ...prev, ...updates }));
  }, []);

  const handleSelectLogo = useCallback((asset: BrandingAsset) => {
    setSelectedLogoId(asset.id);
    updateBranding({ logo: null, logoUrl: asset.publicUrl });
  }, [updateBranding]);

  const handleSelectOverlay = useCallback((asset: BrandingAsset) => {
    setSelectedOverlayId(asset.id);
    updateBranding({ overlay: null, overlayUrl: asset.publicUrl });
  }, [updateBranding]);

  const handleSelectWatermark = useCallback((asset: BrandingAsset) => {
    setSelectedWatermarkId(asset.id);
    updateBranding({ watermarkUrl: asset.publicUrl });
  }, [updateBranding]);

  const removeLogo = useCallback(() => {
    setSelectedLogoId(null);
    updateBranding({ logo: null, logoUrl: null });
  }, [updateBranding]);

  const removeOverlay = useCallback(() => {
    setSelectedOverlayId(null);
    updateBranding({ overlay: null, overlayUrl: null });
  }, [updateBranding]);

  // Overlay-Builder öffnen und danach hierher (Overlay-Tool) zurückkehren — NICHT
  // in den Staging-Flow. Vollnavigation; das neue Overlay ist als Standard gesetzt
  // und wird nach dem Rücksprung geladen.
  const handleOpenOverlayBuilder = useCallback(() => {
    window.location.assign('/app/overlay-builder?return=' + encodeURIComponent('/app/overlay-tool'));
  }, []);

  const removeWatermark = useCallback(() => {
    setSelectedWatermarkId(null);
    updateBranding({ watermarkUrl: null });
  }, [updateBranding]);

  // ============================================================================
  // Crop Modal Handlers
  // ============================================================================

  // Compute the percentage-space ratio constraint: w = h * pctRatio
  const getCropPctRatio = useCallback((imgW: number, imgH: number) => {
    const targetRatio = aspectRatio === '1:1' ? 1 : 4 / 3;
    return (targetRatio * imgH) / imgW;
  }, [aspectRatio]);

  const openCropModal = useCallback((img: UploadedFile) => {
    setCropModalImage(img);
    setCropImgDims(null); // will be set by onLoad
  }, []);

  // Called when the image in the crop modal finishes loading
  const handleCropImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const imgEl = e.currentTarget;
    const natW = imgEl.naturalWidth;
    const natH = imgEl.naturalHeight;
    setCropImgDims({ w: natW, h: natH });

    const pctRatio = getCropPctRatio(natW, natH);
    const offset = cropModalImage?.cropOffset ?? { x: 0.5, y: 0.5 };
    const scale = cropModalImage?.cropScale ?? 1;

    // Compute max frame in pct space
    let maxH: number;
    let maxW: number;
    if (pctRatio <= 1) {
      // Frame is narrower or equal to image width
      maxH = 1;
      maxW = pctRatio;
    } else {
      // Frame is wider than image — constrain by width
      maxW = 1;
      maxH = 1 / pctRatio;
    }

    const w = maxW * scale;
    const h = maxH * scale;
    const x = (1 - w) * offset.x;
    const y = (1 - h) * offset.y;

    setCropRect({ x, y, w, h });
  }, [cropModalImage, getCropPctRatio]);

  const handleCropApply = useCallback(async () => {
    if (!cropModalImage || !cropImgDims) return;

    const pctRatio = getCropPctRatio(cropImgDims.w, cropImgDims.h);

    // Compute max frame
    let maxW: number;
    let maxH: number;
    if (pctRatio <= 1) {
      maxH = 1; maxW = pctRatio;
    } else {
      maxW = 1; maxH = 1 / pctRatio;
    }

    const scale = Math.min(cropRect.w / maxW, cropRect.h / maxH);
    const offsetX = cropRect.w < 1 - 0.001 ? cropRect.x / (1 - cropRect.w) : 0.5;
    const offsetY = cropRect.h < 1 - 0.001 ? cropRect.y / (1 - cropRect.h) : 0.5;
    const clampedOffset = {
      x: Math.max(0, Math.min(1, offsetX)),
      y: Math.max(0, Math.min(1, offsetY)),
    };

    try {
      const newPreview = await cropToAspectRatio(
        cropModalImage.originalUrl,
        aspectRatio,
        clampedOffset,
        scale,
        PREVIEW_CROP_OUTPUT
      );
      setUploadedImages(prev =>
        prev.map(img => {
          if (img.id !== cropModalImage.id) return img;
          if (img.previewUrl !== img.originalUrl && img.previewUrl.startsWith('blob:')) {
            URL.revokeObjectURL(img.previewUrl);
          }
          return { ...img, previewUrl: newPreview, cropOffset: clampedOffset, cropScale: scale };
        })
      );
    } catch (e) {
      console.error('[OverlayTool] Crop apply failed:', e);
    }
    setCropModalImage(null);
  }, [cropModalImage, cropImgDims, aspectRatio, cropRect, getCropPctRatio]);

  const handleCropReset = useCallback(async () => {
    if (!cropModalImage) return;
    try {
      const newPreview = await cropToAspectRatio(
        cropModalImage.originalUrl,
        aspectRatio,
        { x: 0.5, y: 0.5 },
        1.0,
        PREVIEW_CROP_OUTPUT
      );
      setUploadedImages(prev =>
        prev.map(img => {
          if (img.id !== cropModalImage.id) return img;
          if (img.previewUrl !== img.originalUrl && img.previewUrl.startsWith('blob:')) {
            URL.revokeObjectURL(img.previewUrl);
          }
          return { ...img, previewUrl: newPreview, cropOffset: { x: 0.5, y: 0.5 }, cropScale: 1.0 };
        })
      );
    } catch (e) {
      console.error('[OverlayTool] Crop reset failed:', e);
    }
    setCropModalImage(null);
  }, [cropModalImage, aspectRatio]);

  // Drag handler: move the crop frame
  const handleCropFrameMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    const container = e.currentTarget.parentElement;
    if (!container) return;
    const containerRect = container.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const startCrop = { ...cropRect };

    const onMove = (ev: MouseEvent) => {
      const dx = (ev.clientX - startX) / containerRect.width;
      const dy = (ev.clientY - startY) / containerRect.height;
      setCropRect({
        ...startCrop,
        x: Math.max(0, Math.min(1 - startCrop.w, startCrop.x + dx)),
        y: Math.max(0, Math.min(1 - startCrop.h, startCrop.y + dy)),
      });
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [cropRect]);

  // Resize handler: resize from a corner or edge while maintaining aspect ratio
  const handleCropResize = useCallback((
    e: React.MouseEvent<HTMLDivElement>,
    anchor: 'tl' | 'tr' | 'bl' | 'br' | 'top' | 'bottom' | 'left' | 'right'
  ) => {
    e.preventDefault();
    e.stopPropagation();
    if (!cropImgDims) return;

    const container = (e.currentTarget.parentElement as HTMLElement)?.parentElement;
    if (!container) return;
    const containerRect = container.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const startCrop = { ...cropRect };
    const pctRatio = getCropPctRatio(cropImgDims.w, cropImgDims.h);
    const minSize = 0.1; // minimum 10% of image

    const onMove = (ev: MouseEvent) => {
      const dx = (ev.clientX - startX) / containerRect.width;
      const dy = (ev.clientY - startY) / containerRect.height;

      let newX = startCrop.x;
      let newY = startCrop.y;
      let newW = startCrop.w;
      let newH = startCrop.h;

      // Determine which dimension to drive based on anchor
      if (anchor === 'br' || anchor === 'right' || anchor === 'bottom') {
        // Fixed: top-left corner
        if (anchor === 'bottom') {
          newH = Math.max(minSize, startCrop.h + dy);
          newW = newH * pctRatio;
        } else {
          newW = Math.max(minSize * pctRatio, startCrop.w + dx);
          newH = newW / pctRatio;
        }
        // Clamp to image bounds
        if (newX + newW > 1) { newW = 1 - newX; newH = newW / pctRatio; }
        if (newY + newH > 1) { newH = 1 - newY; newW = newH * pctRatio; }
      } else if (anchor === 'tl' || anchor === 'left' || anchor === 'top') {
        // Fixed: bottom-right corner
        const fixedRight = startCrop.x + startCrop.w;
        const fixedBottom = startCrop.y + startCrop.h;
        if (anchor === 'top') {
          newH = Math.max(minSize, startCrop.h - dy);
          newW = newH * pctRatio;
        } else {
          newW = Math.max(minSize * pctRatio, startCrop.w - dx);
          newH = newW / pctRatio;
        }
        newX = fixedRight - newW;
        newY = fixedBottom - newH;
        if (newX < 0) { newX = 0; newW = fixedRight; newH = newW / pctRatio; newY = fixedBottom - newH; }
        if (newY < 0) { newY = 0; newH = fixedBottom; newW = newH * pctRatio; newX = fixedRight - newW; }
      } else if (anchor === 'tr') {
        // Fixed: bottom-left corner
        const fixedBottom = startCrop.y + startCrop.h;
        newW = Math.max(minSize * pctRatio, startCrop.w + dx);
        newH = newW / pctRatio;
        newY = fixedBottom - newH;
        if (newX + newW > 1) { newW = 1 - newX; newH = newW / pctRatio; newY = fixedBottom - newH; }
        if (newY < 0) { newY = 0; newH = fixedBottom; newW = newH * pctRatio; }
      } else if (anchor === 'bl') {
        // Fixed: top-right corner
        const fixedRight = startCrop.x + startCrop.w;
        newW = Math.max(minSize * pctRatio, startCrop.w - dx);
        newH = newW / pctRatio;
        newX = fixedRight - newW;
        if (newX < 0) { newX = 0; newW = fixedRight; newH = newW / pctRatio; }
        if (newY + newH > 1) { newH = 1 - newY; newW = newH * pctRatio; newX = fixedRight - newW; }
      }

      // Final minimum size check
      if (newW < minSize * pctRatio || newH < minSize) return;

      setCropRect({ x: newX, y: newY, w: newW, h: newH });
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [cropRect, cropImgDims, getCropPctRatio]);

  // ============================================================================
  // Export Handler
  // ============================================================================

  const handleExport = useCallback(async (compressed: boolean) => {
    if (uploadedImages.length === 0) return;

    // Warnen, wenn das Overlay keine Variante fürs Ziel-Format hat.
    if (
      selectedOverlayAsset &&
      overlayNeedsAspectWarning(selectedOverlayAsset, overlays, ASPECT_VALUE[aspectRatio]) &&
      !window.confirm(
        `Das gewählte Overlay hat keine ${aspectRatio}-Variante. Es wird verzerrt auf die Bilder gelegt. Trotzdem exportieren?`
      )
    ) {
      return;
    }

    setIsProcessing(true);
    const suffix = compressed ? '_komprimiert' : '_original';
    const folderName = projectName ? `${projectName.replace(/\s+/g, '_')}_branded${suffix}` : `branded_images${suffix}`;
    const ext = compressed ? 'jpg' : 'png';

    try {
      const imageByPath = new Map<string, (typeof uploadedImages)[number]>();
      const planned: PlannedFile[] = uploadedImages.map((img) => {
        const path = `${folderName}/${img.name}_branded.${ext}`;
        imageByPath.set(path, img);
        return { path, source: img.previewUrl };
      });

      const result = await assembleExportZip(planned, {
        transform: async (_source, file) => {
          const img = imageByPath.get(file.path)!;
          // Gecroppte Bilder: volle Auflösung erst hier rechnen — der State hält
          // nur die 1600px-Vorschau (Full-Res-Data-URLs froren die Seite ein).
          const exportSource = img.needsCrop
            ? await cropToAspectRatio(img.originalUrl, aspectRatio, img.cropOffset ?? { x: 0.5, y: 0.5 }, img.cropScale ?? 1.0)
            : img.previewUrl;
          // Gleiche Optionen wie die große Vorschau — Vorschau = Export.
          const branded = await renderBrandedExport(exportSource, exportBrandingOptions, compressed ? { aspectRatio } : undefined);
          // KI-Hinweis zuletzt (nach Branding + Kompression) und nur auf
          // Projektbildern — hochgeladene Makler-Fotos sind keine KI-Ergebnisse.
          return aiLabel.enabled && overlayImageIsAiResult(img)
            ? applyAiLabelToImage(branded, aiLabel.position, aiLabel.opacity)
            : branded;
        },
      });
      saveAs(result.blob, `${folderName}.zip`);
      if (result.failed > 0) {
        alert(`${result.added} von ${result.total} Dateien exportiert — einige Bilder konnten nicht verarbeitet werden.`);
      }

      // Zuletzt genutzte Auswahl als Standard merken — beim nächsten Besuch
      // (auch im Wizard/Workspace) ist das Wasserzeichen dann vorausgewählt.
      if (user?.id) {
        const settings: DefaultBrandingSettings = {
          watermarkOpacity: branding.watermarkOpacity,
          watermarkScale: Math.round(branding.watermarkScale * 100),
          watermarkPosition: branding.watermarkPosition,
          logoPosition: branding.logoPosition,
        };
        // Lokale Blob-Uploads haben keine Asset-ID — Default dann nicht anfassen.
        if (selectedWatermarkId || !branding.watermarkUrl) settings.watermarkAssetId = selectedWatermarkId;
        if (selectedLogoId || !branding.logoUrl) settings.logoAssetId = selectedLogoId;
        void updateDefaultBrandingDefaults(user.id, settings);
      }
    } catch (error) {
      console.error('Export failed:', error);
      // Abbrechen statt still ohne Wasserzeichen/Logo/Overlay zu exportieren.
      alert(
        error instanceof BrandingLayerError
          ? 'Das Branding (z. B. Wasserzeichen) konnte nicht angewendet werden — Export abgebrochen. Tipp: dunkles Logo auf weißem oder transparentem Hintergrund verwenden, oder die Auswahl entfernen.'
          : 'Fehler beim Export der Bilder'
      );
    } finally {
      setIsProcessing(false);
    }
  }, [uploadedImages, branding, projectName, aspectRatio, selectedOverlayAsset, overlays, exportBrandingOptions, user?.id, selectedWatermarkId, selectedLogoId, aiLabel.enabled, aiLabel.position, aiLabel.opacity]);

  // ============================================================================
  // Position Buttons
  // ============================================================================

  const logoPositionButtons: { position: LogoPosition; icon: React.ReactNode }[] = [
    { position: 'top-left', icon: <AlignStartVertical className="w-4 h-4" /> },
    { position: 'top-right', icon: <AlignEndVertical className="w-4 h-4" /> },
    { position: 'bottom-left', icon: <AlignStartHorizontal className="w-4 h-4" /> },
    { position: 'bottom-right', icon: <AlignEndHorizontal className="w-4 h-4" /> },
  ];

  const watermarkPositionButtons: { position: WatermarkPosition; icon: React.ReactNode; label: string }[] = [
    { position: 'center', icon: <Square className="w-3 h-3" />, label: 'Mitte' },
    { position: 'tile', icon: <Grid3X3 className="w-3 h-3" />, label: 'Kachel' },
    { position: 'tl', icon: <CornerLeftUp className="w-3 h-3" />, label: 'OL' },
    { position: 'tr', icon: <CornerRightUp className="w-3 h-3" />, label: 'OR' },
    { position: 'bl', icon: <CornerLeftDown className="w-3 h-3" />, label: 'UL' },
    { position: 'br', icon: <CornerRightDown className="w-3 h-3" />, label: 'UR' },
  ];

  // ============================================================================
  // Render Helper: Asset Grid
  // ============================================================================

  // V3-Muster (ProjectWizardView.renderAssetPicker): Thumbnail-Grid der Bibliothek
  // + Upload-Kachel. Der aktive Eigen-Upload (lokale Datei, kein Bibliotheks-Asset)
  // erscheint als hervorgehobene „Neu"-Kachel vorne.
  const renderBrandingPicker = (
    assets: BrandingAsset[],
    selectedId: string | null,
    activeLocalUrl: string | null,
    onSelect: (asset: BrandingAsset) => void,
    onLocalUpload: (file: File) => void,
    onRemove: () => void,
    accept: string
  ) => {
    const hasLocal = !!activeLocalUrl && !selectedId;
    return (
      <div className="grid grid-cols-3 gap-2">
        {hasLocal && (
          <button
            type="button"
            onClick={onRemove}
            title="Eigener Upload — Klick entfernt die Auswahl"
            className="relative aspect-[4/3] overflow-hidden rounded-lg border-2 border-primary bg-muted/30 ring-2 ring-primary/20"
          >
            <img src={activeLocalUrl!} alt="Upload" className="h-full w-full object-contain p-1" />
            <span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-foreground shadow">
              <Check className="h-2.5 w-2.5" />
            </span>
            <span className="absolute bottom-1 left-1 rounded bg-black/60 px-1 py-0.5 text-[8px] font-semibold uppercase tracking-wide text-white">
              Neu
            </span>
          </button>
        )}
        {isLoadingAssets ? (
          <div className="col-span-3 flex items-center justify-center rounded-lg border border-border bg-muted/30 py-4 text-xs text-muted-foreground">
            <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> Wird geladen…
          </div>
        ) : (
          assets.map((asset) => {
            const active = asset.id === selectedId;
            return (
              <button
                key={asset.id}
                type="button"
                onClick={() => (active ? onRemove() : onSelect(asset))}
                title={active ? `${asset.name} — Klick entfernt die Auswahl` : asset.name}
                className={cn(
                  'relative aspect-[4/3] overflow-hidden rounded-lg border-2 bg-muted/30 transition-all',
                  active ? 'border-primary ring-2 ring-primary/20' : 'border-border hover:border-primary/40'
                )}
              >
                <img src={asset.publicUrl} alt={asset.name} className="h-full w-full object-contain p-1" />
                {active && (
                  <span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-foreground shadow">
                    <Check className="h-2.5 w-2.5" />
                  </span>
                )}
              </button>
            );
          })
        )}
        <label className="flex aspect-[4/3] cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-border text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary">
          <Upload className="h-4 w-4" />
          <span className="text-[10px] font-medium">Hochladen</span>
          <input
            type="file"
            className="hidden"
            accept={accept}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) onLocalUpload(file);
            }}
          />
        </label>
      </div>
    );
  };

  // ============================================================================
  // Render
  // ============================================================================

  // Auswahl-Liste: pro Overlay nur eine Variante (4:3 bevorzugt) — 1:1 wird
  // beim Export automatisch aufgelöst, nicht separat angezeigt.
  const displayOverlays = overlaysForSelection(overlays);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          {onBack && (
            <Button
              variant="ghost"
              onClick={onBack}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-2 pl-0"
            >
              <ArrowLeft className="w-4 h-4" />
              Zurück
            </Button>
          )}
          <h1 className="text-2xl font-bold text-foreground">Overlay anwenden</h1>
          <p className="text-muted-foreground mt-1">
            {projectName 
              ? `Branding für "${projectName}" anwenden` 
              : 'Wende Logo, Overlay & Wasserzeichen auf Bilder an'
            }
          </p>
        </div>

        {uploadedImages.length > 0 && (
          <span className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm font-semibold text-foreground">
            {uploadedImages.length} {uploadedImages.length === 1 ? 'Bild' : 'Bilder'}
          </span>
        )}
      </div>

      {/* Loading State */}
      {isLoadingProjectImages && (
        <Card className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
          <span className="ml-3 text-muted-foreground">Projektbilder werden geladen...</span>
        </Card>
      )}

      {!isLoadingProjectImages && (
        <div className="grid items-start gap-6 lg:grid-cols-[minmax(320px,380px)_1fr]">
          {/* Left Column: Branding Setup */}
          <div className="space-y-4">
            {/* Branding-Steuerung — Accordion (V3-Muster: Branding & Download) */}
            <Accordion
              type="multiple"
              defaultValue={['logo', 'overlay', 'watermark']}
              className="rounded-xl border border-border bg-card px-4"
            >
              {/* Logo */}
              <AccordionItem value="logo">
                <AccordionTrigger className="text-sm font-semibold">
                  <span className="flex items-center gap-2">
                    <ImageIcon className="h-4 w-4 text-primary" /> Logo
                    {branding.logoUrl && <Badge variant="secondary" className="text-[10px]">aktiv</Badge>}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="space-y-3">
                  {renderBrandingPicker(
                    logos,
                    selectedLogoId,
                    branding.logoUrl,
                    handleSelectLogo,
                    (file) => {
                      const url = URL.createObjectURL(file);
                      updateBranding({ logoUrl: url });
                      setSelectedLogoId(null);
                    },
                    removeLogo,
                    'image/png,image/jpeg'
                  )}
                  {branding.logoUrl && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Position</Label>
                      <div className="mt-1.5 grid grid-cols-4 gap-1.5">
                        {logoPositionButtons.map(({ position, icon }) => (
                          <button
                            key={position}
                            type="button"
                            onClick={() => updateBranding({ logoPosition: position })}
                            className={cn(
                              'flex items-center justify-center rounded-lg border-2 py-1.5 transition-colors',
                              branding.logoPosition === position
                                ? 'border-primary bg-primary/5 text-primary'
                                : 'border-border text-muted-foreground hover:border-primary/40'
                            )}
                          >
                            {icon}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>

              {/* Overlay */}
              <AccordionItem value="overlay">
                <AccordionTrigger className="text-sm font-semibold">
                  <span className="flex items-center gap-2">
                    <Square className="h-4 w-4 text-primary" /> Overlay
                    {branding.overlayUrl && <Badge variant="secondary" className="text-[10px]">aktiv</Badge>}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="space-y-3">
                  {displayOverlays.length === 0 && !isLoadingAssets ? (
                    /* Noch kein Overlay in der Bibliothek → prominenter Builder-CTA */
                    <button
                      type="button"
                      onClick={handleOpenOverlayBuilder}
                      className="flex w-full items-center gap-3 rounded-xl border-2 border-primary/40 bg-primary/5 p-3 text-left transition-colors hover:bg-primary/10"
                    >
                      <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <Sparkles className="h-4 w-4" />
                      </span>
                      <span>
                        <span className="block text-sm font-semibold text-foreground">Overlay erstellen</span>
                        <span className="block text-[11px] text-muted-foreground">
                          Noch kein Overlay — im Builder aus einer Vorlage erstellen.
                        </span>
                      </span>
                    </button>
                  ) : (
                    renderBrandingPicker(
                      displayOverlays,
                      selectedOverlayId,
                      branding.overlayUrl,
                      handleSelectOverlay,
                      (file) => {
                        const url = URL.createObjectURL(file);
                        updateBranding({ overlay: null, overlayUrl: url });
                        setSelectedOverlayId(null);
                      },
                      removeOverlay,
                      'image/png'
                    )
                  )}

                  {displayOverlays.length > 0 && (
                    <button
                      type="button"
                      onClick={handleOpenOverlayBuilder}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
                    >
                      <Sparkles className="h-3.5 w-3.5" /> Neues Overlay erstellen
                    </button>
                  )}

                  <p className="text-[11px] text-muted-foreground">
                    PNG mit Transparenz, gleiches Format wie die Bilder ({aspectRatio}).
                  </p>
                </AccordionContent>
              </AccordionItem>

              {/* Wasserzeichen */}
              <AccordionItem value="watermark" className="border-b-0">
                <AccordionTrigger className="text-sm font-semibold">
                  <span className="flex items-center gap-2">
                    <Droplets className="h-4 w-4 text-primary" /> Wasserzeichen
                    {branding.watermarkUrl && <Badge variant="secondary" className="text-[10px]">aktiv</Badge>}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="space-y-3">
                  {renderBrandingPicker(
                    watermarks,
                    selectedWatermarkId,
                    branding.watermarkUrl,
                    handleSelectWatermark,
                    (file) => {
                      const url = URL.createObjectURL(file);
                      updateBranding({ watermarkUrl: url });
                      setSelectedWatermarkId(null);
                    },
                    removeWatermark,
                    'image/png,image/jpeg'
                  )}
                  {branding.watermarkUrl && (
                    <>
                      <div>
                        <div className="mb-1.5 flex items-center justify-between">
                          <Label className="text-xs text-muted-foreground">Deckkraft</Label>
                          <span className="font-mono text-xs">{branding.watermarkOpacity}%</span>
                        </div>
                        <Slider
                          min={5}
                          max={80}
                          step={5}
                          value={[branding.watermarkOpacity]}
                          onValueChange={([value]) => updateBranding({ watermarkOpacity: value })}
                        />
                      </div>
                      <div>
                        <div className="mb-1.5 flex items-center justify-between">
                          <Label className="text-xs text-muted-foreground">Größe</Label>
                          <span className="font-mono text-xs">{Math.round(branding.watermarkScale * 100)}%</span>
                        </div>
                        <Slider
                          min={10}
                          max={100}
                          step={5}
                          value={[branding.watermarkScale * 100]}
                          onValueChange={([value]) => updateBranding({ watermarkScale: value / 100 })}
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Position</Label>
                        <div className="mt-1.5 grid grid-cols-3 gap-1.5">
                          {watermarkPositionButtons.map(({ position, icon, label }) => (
                            <button
                              key={position}
                              type="button"
                              onClick={() => updateBranding({ watermarkPosition: position })}
                              className={cn(
                                'flex flex-col items-center gap-0.5 rounded-lg border-2 py-1.5 text-[10px] transition-colors',
                                branding.watermarkPosition === position
                                  ? 'border-primary bg-primary/5 text-primary'
                                  : 'border-border text-muted-foreground hover:border-primary/40'
                              )}
                            >
                              {icon}
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <p className="text-[11px] text-muted-foreground">
                        Weiß wird transparent, Schwarz behält die Deckkraft.
                      </p>
                    </>
                  )}
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            {/* Letzte Export-Option direkt über den Download-Knöpfen */}
            <AiLabelOption {...aiLabel} className="mt-4" />
            {aiLabel.enabled && aiLabeledCount < uploadedImages.length && (
              <p className="pl-9 pt-2 text-[11px] text-muted-foreground">
                {aiLabeledCount === 0
                  ? 'Deine hochgeladenen Fotos bleiben unverändert — der Hinweis gilt nur für Bilder aus dem Projekt.'
                  : `Nur die ${aiLabeledCount} Bilder aus dem Projekt — deine hochgeladenen Fotos bleiben unverändert.`}
              </p>
            )}

            {/* Export Buttons */}
            <div className="mt-3 space-y-2">
              <Button
                onClick={() => handleExport(true)}
                disabled={uploadedImages.length === 0 || isProcessing}
                className="w-full flex items-center justify-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Verarbeitung...
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5" />
                    Komprimiert herunterladen
                  </>
                )}
              </Button>
              <Button
                onClick={() => handleExport(false)}
                disabled={uploadedImages.length === 0 || isProcessing}
                variant="outline"
                className="w-full flex items-center justify-center gap-2"
              >
                <Download className="w-4 h-4" />
                Original herunterladen
              </Button>
              {uploadedImages.length > 0 && (
                <p className="text-xs text-muted-foreground text-center">{uploadedImages.length} Bilder</p>
              )}
            </div>
          </div>

          {/* Right Column: Image Upload & Preview */}
          <div>
            <Card className="h-full">
              <CardContent className="p-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-bold text-lg text-foreground">Bilder</h3>
                  {uploadedImages.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearAllImages}
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Alle entfernen
                    </Button>
                  )}
                </div>

                {/* Drop Zone */}
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onClick={() => fileInputRef.current?.click()}
                  className={`
                    border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all
                    ${isDragging 
                      ? 'border-primary bg-primary/10' 
                      : 'border-border hover:border-primary hover:bg-muted'
                    }
                  `}
                >
                  <Upload className={`w-10 h-10 mx-auto mb-3 ${isDragging ? 'text-primary' : 'text-muted-foreground/50'}`} />
                  <p className="text-foreground font-medium">
                    {isDragging ? 'Bilder hier ablegen' : 'Bilder hier ablegen oder klicken'}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">PNG, JPG (Mehrfachauswahl möglich)</p>
                  <Input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleFileSelect(e.target.files)}
                  />
                </div>

                {/* Große Export-Vorschau: echtes Compositing (= Export-Ergebnis) */}
                {previewImage && (
                  <div className="mt-6">
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        So sieht dein Export aus
                      </p>
                      {uploadedImages.length > 1 && (
                        <p className="text-[11px] text-muted-foreground">
                          Bild unten anklicken, um es hier groß zu prüfen
                        </p>
                      )}
                    </div>
                    <div
                      className={`relative w-full overflow-hidden rounded-xl border border-border bg-muted ${
                        aspectRatio === '1:1' ? 'aspect-square' : 'aspect-[4/3]'
                      }`}
                    >
                      <img
                        src={finalPreview?.url ?? previewImage.previewUrl}
                        alt="Export-Vorschau"
                        className="w-full h-full object-cover"
                      />
                      {isRenderingPreview && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/10">
                          <RefreshCw className="w-6 h-6 animate-spin text-white drop-shadow" />
                        </div>
                      )}
                    </div>
                    {finalPreview && finalPreview.failedLayers.length > 0 && (
                      <p className="mt-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                        Das Wasserzeichen/Branding konnte nicht angewendet werden und würde beim Export fehlen.
                        Tipp: dunkles Logo auf weißem oder transparentem Hintergrund verwenden.
                      </p>
                    )}
                  </div>
                )}

                {/* Image Grid with Live Preview */}
                {uploadedImages.length > 0 && (
                  <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {uploadedImages.map((img) => (
                      <div
                        key={img.id}
                        onClick={() => setPreviewImageId(img.id)}
                        className={cn(
                          `relative group cursor-pointer ${aspectRatio === '1:1' ? 'aspect-square' : 'aspect-[4/3]'}`,
                          previewImage?.id === img.id && 'ring-2 ring-primary rounded-lg'
                        )}
                        title="Groß in der Vorschau anzeigen"
                      >
                        <img
                          src={img.previewUrl}
                          alt="Preview"
                          className="w-full h-full object-cover rounded-lg"
                        />

                        {/* Variant Badge */}
                        {img.isFromProject && img.variant && (
                          <div className={`absolute top-1 left-1 text-[10px] text-white px-1.5 py-0.5 rounded ${
                            img.variant.includes('floorplan') ? 'bg-teal-500'
                              : img.variant.includes('empty') ? 'bg-blue-500' : 'bg-green-500'
                          }`}>
                            {img.variant === 'floorplan_2d' ? '2D Grundriss'
                              : img.variant === 'floorplan_3d' ? '3D Grundriss'
                                : img.variant.includes('empty') ? 'Leer' : 'Staged'}
                          </div>
                        )}

                        {/* Watermark Preview */}
                        {watermarkPreviewUrl && branding.watermarkUrl && (
                          <div
                            className="absolute inset-0 pointer-events-none flex items-center justify-center"
                            style={{
                              ...(branding.watermarkPosition === 'tile' && {
                                backgroundImage: `url(${watermarkPreviewUrl})`,
                                backgroundRepeat: 'repeat',
                                backgroundSize: `${branding.watermarkScale * 40}%`,
                              }),
                            }}
                          >
                            {branding.watermarkPosition !== 'tile' && (
                              <img
                                src={watermarkPreviewUrl}
                                alt=""
                                style={{
                                  width: `${branding.watermarkScale * 40}%`,
                                  position: 'absolute',
                                  ...(branding.watermarkPosition === 'center' && { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }),
                                  ...(branding.watermarkPosition === 'tl' && { top: '5%', left: '5%' }),
                                  ...(branding.watermarkPosition === 'tr' && { top: '5%', right: '5%' }),
                                  ...(branding.watermarkPosition === 'bl' && { bottom: '5%', left: '5%' }),
                                  ...(branding.watermarkPosition === 'br' && { bottom: '5%', right: '5%' }),
                                }}
                              />
                            )}
                          </div>
                        )}

                        {/* Overlay Preview */}
                        {branding.overlayUrl && (
                          <img
                            src={branding.overlayUrl}
                            className="absolute inset-0 w-full h-full object-cover pointer-events-none opacity-80"
                            alt=""
                          />
                        )}

                        {/* Logo Preview */}
                        {branding.logoUrl && (
                          <div
                            className={`absolute w-[20%] pointer-events-none
                              ${branding.logoPosition === 'top-left' ? 'top-[5%] left-[5%]' : ''}
                              ${branding.logoPosition === 'top-right' ? 'top-[5%] right-[5%]' : ''}
                              ${branding.logoPosition === 'bottom-left' ? 'bottom-[5%] left-[5%]' : ''}
                              ${branding.logoPosition === 'bottom-right' ? 'bottom-[5%] right-[5%]' : ''}
                            `}
                          >
                            <img src={branding.logoUrl} className="w-full h-auto opacity-80" alt="" />
                          </div>
                        )}

                        {/* Crop Button (shown on hover when image was cropped) */}
                        {img.needsCrop && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openCropModal(img);
                            }}
                            className="absolute bottom-2 left-2 p-1.5 bg-black/50 hover:bg-primary text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Ausschnitt anpassen"
                          >
                            <Crop className="w-3 h-3" />
                          </button>
                        )}

                        {/* Remove Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeImage(img.id);
                          }}
                          className="absolute top-2 right-2 p-1.5 bg-black/50 hover:bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {uploadedImages.length === 0 && !projectId && (
                  <div className="mt-6 text-center text-muted-foreground py-8">
                    <ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Noch keine Bilder hochgeladen</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Crop Modal */}
      {cropModalImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-background rounded-xl shadow-xl max-w-lg w-full mx-4 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <Move className="w-5 h-5 text-primary" />
                Ausschnitt anpassen
              </h3>
              <button onClick={() => setCropModalImage(null)} className="text-muted-foreground hover:text-foreground">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Crop Preview Area */}
            <div className="relative overflow-hidden rounded-lg bg-black select-none">
              <div className="relative">
                <img
                  src={cropModalImage.originalUrl}
                  alt="Original"
                  className="w-full h-auto block"
                  draggable={false}
                  onLoad={handleCropImageLoad}
                />

                {/* Crop frame with darkened outside */}
                {cropImgDims && (
                  <div
                    className="absolute border-2 border-white cursor-move"
                    style={{
                      left: `${cropRect.x * 100}%`,
                      top: `${cropRect.y * 100}%`,
                      width: `${cropRect.w * 100}%`,
                      height: `${cropRect.h * 100}%`,
                      boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.55)',
                    }}
                    onMouseDown={handleCropFrameMove}
                  >
                    {/* Rule of thirds grid lines */}
                    <div className="absolute inset-0 pointer-events-none">
                      <div className="absolute left-1/3 top-0 bottom-0 w-px bg-white/30" />
                      <div className="absolute left-2/3 top-0 bottom-0 w-px bg-white/30" />
                      <div className="absolute top-1/3 left-0 right-0 h-px bg-white/30" />
                      <div className="absolute top-2/3 left-0 right-0 h-px bg-white/30" />
                    </div>

                    {/* Corner handles */}
                    <div className="absolute -top-1.5 -left-1.5 w-4 h-4 border-t-[3px] border-l-[3px] border-white rounded-tl-sm cursor-nw-resize"
                         onMouseDown={(e) => handleCropResize(e, 'tl')} />
                    <div className="absolute -top-1.5 -right-1.5 w-4 h-4 border-t-[3px] border-r-[3px] border-white rounded-tr-sm cursor-ne-resize"
                         onMouseDown={(e) => handleCropResize(e, 'tr')} />
                    <div className="absolute -bottom-1.5 -left-1.5 w-4 h-4 border-b-[3px] border-l-[3px] border-white rounded-bl-sm cursor-sw-resize"
                         onMouseDown={(e) => handleCropResize(e, 'bl')} />
                    <div className="absolute -bottom-1.5 -right-1.5 w-4 h-4 border-b-[3px] border-r-[3px] border-white rounded-br-sm cursor-se-resize"
                         onMouseDown={(e) => handleCropResize(e, 'br')} />

                    {/* Edge handles */}
                    <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-8 h-1.5 bg-white rounded-full cursor-n-resize"
                         onMouseDown={(e) => handleCropResize(e, 'top')} />
                    <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-8 h-1.5 bg-white rounded-full cursor-s-resize"
                         onMouseDown={(e) => handleCropResize(e, 'bottom')} />
                    <div className="absolute top-1/2 -left-1 -translate-y-1/2 w-1.5 h-8 bg-white rounded-full cursor-w-resize"
                         onMouseDown={(e) => handleCropResize(e, 'left')} />
                    <div className="absolute top-1/2 -right-1 -translate-y-1/2 w-1.5 h-8 bg-white rounded-full cursor-e-resize"
                         onMouseDown={(e) => handleCropResize(e, 'right')} />
                  </div>
                )}
              </div>
            </div>

            <p className="text-xs text-muted-foreground text-center">
              Rahmen verschieben oder an Ecken/Kanten ziehen zum Skalieren
            </p>

            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={handleCropReset}>
                Zurücksetzen
              </Button>
              <Button onClick={handleCropApply}>
                <Check className="w-4 h-4 mr-1" />
                Übernehmen
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
